import { get } from 'vuex-pathify'

// Persists eligibility across market pages so the promo tab stays visible
// after the member has been through the post-customization flow.
const PROMO_ELIGIBLE_KEY = 'alltrue_promo_eligible'

// Stores which A/B variant the member was assigned so they always see the same one.
const PROMO_VARIANT_KEY = 'alltrue_promo_variant'

// 1-hour cookie that suppresses the auto-popup after the member has seen it.
// The promo tab still works regardless of this cookie.
const PROMO_SEEN_KEY = 'alltrue_promo_seen'

export default {
  data() {
    return {
      showPromoModal: false,
      promoVariant: 'a',
      isPromoEligible: false,
    }
  },
  computed: {
    // Prismic: market_landing_page → "Market Promo" tab
    ...get('prismic', ['marketLandingData']),

    // Prismic field: promo_enabled (Boolean)
    // Description: Enables or disables the promo modal for all eligible members.
    // Toggle off at any time without a code deploy.
    promoEnabled() {
      return this.marketLandingData?.promo_enabled ?? false
    },

    // Prismic field: promo_tab_label (Key Text)
    // Description: Text shown on the sticky tab on the left edge of market pages.
    // Change to match the current promotion (e.g. "Feedback", "Get Offer", "Claim Gift").
    promoTabLabel() {
      return this.marketLandingData?.promo_tab_label || 'Feedback'
    },

    // Prismic field: promo_cta_url / promo_cta_url_b (Link — allow external)
    // Description: Where the primary action button links to. Opens in a new tab.
    // The _b variant is used for members assigned to Variant B in the A/B test.
    promoUrl() {
      const data = this.marketLandingData
      if (!data) return ''
      if (this.promoVariant === 'b' && data.promo_cta_url_b?.url) {
        return data.promo_cta_url_b.url
      }
      return data.promo_cta_url?.url || ''
    },

    // Prismic field: promo_headline / promo_headline_b (Key Text)
    // Description: Main headline shown in the modal. Keep under ~60 characters for best display.
    // The _b variant is used for members assigned to Variant B in the A/B test.
    promoHeadline() {
      const data = this.marketLandingData
      if (!data) return ''
      if (this.promoVariant === 'b' && data.promo_headline_b) {
        return data.promo_headline_b
      }
      return data.promo_headline || ''
    },

    // Prismic field: promo_subhead / promo_subhead_b (Key Text)
    // Description: Supporting copy below the headline. Explains the offer and the next step.
    // The _b variant is used for members assigned to Variant B in the A/B test.
    promoSubhead() {
      const data = this.marketLandingData
      if (!data) return ''
      if (this.promoVariant === 'b' && data.promo_subhead_b) {
        return data.promo_subhead_b
      }
      return data.promo_subhead || ''
    },

    // Prismic field: promo_cta_text / promo_cta_text_b (Key Text)
    // Description: Label for the primary action button.
    // Examples: "Start My 2-Minute Survey", "Get My Promo Code", "Claim My Free Gift".
    promoCtaText() {
      const data = this.marketLandingData
      if (!data) return ''
      if (this.promoVariant === 'b' && data.promo_cta_text_b) {
        return data.promo_cta_text_b
      }
      return data.promo_cta_text || ''
    },

    // Prismic field: promo_dismiss_text (Key Text)
    // Description: Text for the link that closes the modal without taking action.
    // Example: "No thanks, maybe later".
    promoDismissText() {
      return this.marketLandingData?.promo_dismiss_text || 'No thanks, maybe later'
    },
  },
  watch: {
    // Initialise eligibility once Prismic data arrives. Using a watcher instead of
    // created() because the Prismic fetch is async — data isn't available yet when
    // created() runs on Market and MarketProduct pages.
    marketLandingData: {
      immediate: true,
      handler(data) {
        if (!data || !this.promoEnabled) return
        this.isPromoEligible = localStorage.getItem(PROMO_ELIGIBLE_KEY) === 'true'
        if (this.isPromoEligible) {
          this.promoVariant = localStorage.getItem(PROMO_VARIANT_KEY) || 'a'
        }
      },
    },
  },
  created() {
    // Ensure Prismic data is loaded on pages that don't fetch it themselves
    // (Market and MarketProduct). MarketLanding already dispatches this.
    if (!this.marketLandingData) {
      this.$store.dispatch('prismic/loadMarketLandingData')
    }
  },
  methods: {
    handlePromoDismiss() {
      this.showPromoModal = false
      const expires = new Date(Date.now() + 60 * 60 * 1000).toUTCString()
      document.cookie = `${PROMO_SEEN_KEY}=1; expires=${expires}; path=/`
    },
    openPromo() {
      this.showPromoModal = true
    },
    isPromoSuppressed() {
      return document.cookie.split(';').some((c) => c.trim().startsWith(`${PROMO_SEEN_KEY}=`))
    },
  },
}
